/**
 * Real Estate Page
 * Clean listing. Minimal filters. No visual clutter.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatPriceRange, formatArea, formatBedrooms } from '../lib/utils';
import Button from '../components/common/Button';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Container, Section, Spacer } from '../components/primitives';
import { Headline, Title, BodyLarge, Body, Label } from '../components/primitives/Typography';
import type { Project } from '../types';

export default function RealEstate() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [projects, setProjects] = useState<Project[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });

  const typeFilter = searchParams.get('type') || '';
  const cityFilter = searchParams.get('city') || '';
  const page = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [projectsRes, citiesRes] = await Promise.all([
          api.getProjects({
            type: typeFilter || undefined,
            city: cityFilter || undefined,
            page,
            limit: 12,
          }),
          api.getCities(),
        ]);
        setProjects(projectsRes.projects);
        setPagination(projectsRes.pagination);
        setCities(citiesRes.cities);
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [typeFilter, cityFilter, page]);

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const projectTypes = [
    { value: '', label: 'All types' },
    { value: 'apartment', label: 'Apartments' },
    { value: 'villa', label: 'Villas' },
    { value: 'plot', label: 'Plots' },
  ];

  return (
    <main>
      {/* ========== Hero ========== */}
      <Section size="sm" background="gradient" className="pt-20 md:pt-28 pb-12 md:pb-16">
        <Container>
          <div className="max-w-3xl">
            <Label>Real Estate</Label>
            <Headline className="mt-4">
              Apartments and villas in Bangalore and Hyderabad.
            </Headline>
            <BodyLarge className="mt-6">
              Vetted builders only. We don't take commissions from developers.
              Our advice is for your benefit, not theirs.
            </BodyLarge>
          </div>
        </Container>
      </Section>

      {/* ========== Filters ========== */}
      <div className="sticky top-16 md:top-20 z-40 bg-white/80 backdrop-blur-md border-y border-border-subtle shadow-sm">
        <Container>
          <div className="py-4 flex items-center gap-6">
            <select
              value={typeFilter}
              onChange={(e) => updateFilter('type', e.target.value)}
              className="select max-w-[160px]"
            >
              {projectTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            <select
              value={cityFilter}
              onChange={(e) => updateFilter('city', e.target.value)}
              className="select max-w-[160px]"
            >
              <option value="">All cities</option>
              {cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            {(typeFilter || cityFilter) && (
              <button
                onClick={() => setSearchParams({})}
                className="text-small text-text-secondary hover:text-text-primary transition-colors"
              >
                Clear
              </button>
            )}

            <div className="ml-auto text-small text-text-tertiary">
              {pagination.total} {pagination.total === 1 ? 'property' : 'properties'}
            </div>
          </div>
        </Container>
      </div>

      {/* ========== Listings ========== */}
      <Section size="sm">
        <Container>
          {loading ? (
            <PageLoader />
          ) : projects.length === 0 ? (
            <div className="py-20 text-center">
              <Title>No properties found</Title>
              <Body className="mt-2">Try adjusting your filters.</Body>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {projects.map((project) => (
                  <Link key={project.id} to={`/real-estate/${project.slug}`} className="group block bg-white shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                    <div className="aspect-[4/3] bg-surface-muted relative overflow-hidden">
                      {project.hero_image ? (
                        <img
                          src={project.hero_image}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-text-tertiary">
                          <span className="text-small">No image</span>
                        </div>
                      )}
                    </div>

                    <div className="p-5">
                      <div className="flex items-center gap-2">
                        <span className="text-caption font-medium uppercase tracking-wider text-text-tertiary">
                          {project.project_type}
                        </span>
                        <span className="text-text-tertiary">·</span>
                        <span className="text-caption font-medium uppercase tracking-wider text-text-tertiary">
                          {project.city}
                        </span>
                      </div>

                      <h3 className="mt-2 text-h4 font-medium text-text-primary group-hover:text-primary-600 transition-colors">
                        {project.title}
                      </h3>

                      <p className="mt-1 text-small text-text-secondary">
                        {project.location}
                      </p>

                      <div className="mt-3 flex items-center gap-4 text-small text-text-secondary">
                        {formatBedrooms(project.bedrooms_min, project.bedrooms_max) && (
                          <span>{formatBedrooms(project.bedrooms_min, project.bedrooms_max)}</span>
                        )}
                        {formatArea(project.area_min, project.area_max, project.area_unit) && (
                          <span>{formatArea(project.area_min, project.area_max, project.area_unit)}</span>
                        )}
                      </div>

                      <p className="mt-3 text-base font-medium text-primary-600">
                        {formatPriceRange(project.price_min, project.price_max)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="mt-16 flex justify-center gap-2">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('page', p.toString());
                        setSearchParams(newParams);
                      }}
                      className={`
                        w-10 h-10 font-medium transition-all
                        ${p === pagination.page
                          ? 'bg-primary-500 text-white shadow-teal'
                          : 'text-text-secondary hover:text-primary-600 hover:bg-primary-50'
                        }
                      `}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </Container>
      </Section>

      {/* ========== How We Work ========== */}
      <Section background="subtle">
        <Container>
          <div className="max-w-3xl">
            <Label>How we work</Label>
            <Title className="mt-4">Advisory, not brokerage.</Title>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-12 md:gap-8">
            <div>
              <span className="text-h2 font-medium text-text-tertiary">01</span>
              <h3 className="mt-4 text-h4 font-medium text-text-primary">
                Understand your requirements
              </h3>
              <Body className="mt-3">
                Investment vs. residence. City preferences. Budget. Timeline.
                We start with what matters to you.
              </Body>
            </div>

            <div>
              <span className="text-h2 font-medium text-text-tertiary">02</span>
              <h3 className="mt-4 text-h4 font-medium text-text-primary">
                Shortlist and due diligence
              </h3>
              <Body className="mt-3">
                We only recommend projects we've vetted. Builder track record,
                RERA status, title clarity, construction quality.
              </Body>
            </div>

            <div>
              <span className="text-h2 font-medium text-text-tertiary">03</span>
              <h3 className="mt-4 text-h4 font-medium text-text-primary">
                NRI-specific documentation
              </h3>
              <Body className="mt-3">
                FEMA compliance, power of attorney, NRI account setup,
                repatriation planning. We handle the complexity.
              </Body>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== CTA ========== */}
      <Section background="gradient-teal">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <Title>
              Looking for something specific?
            </Title>
            <Body className="mt-4">
              Tell us your requirements. We'll shortlist properties that match.
            </Body>
            <div className="mt-10">
              <Link to="/book">
                <Button variant="primary" size="lg">
                  Book a consultation
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      <Spacer size="sm" />
    </main>
  );
}
